/**
 * Modern 现代模板 - 首页
 * 
 * 风格特点：
 * - 蓝色主色调 (#3B82F6)
 * - 全屏 Hero 区域 + 渐变背景
 * - 分类图标卡片式展示
 * - 信息瀑布流/双列卡片布局
 * - 左侧边栏 + 右侧内容区
 */

import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getSiteInfo, getSiteCategories, getListings, type ListingItem, type FrontendCategory } from "@/utils/api";
import { useSite } from "@/context/SiteContext";

export default function ModernHomePage() {
  const navigate = useNavigate();
  const { siteId, loading: siteLoading } = useSite();
  
  const [searchConfig, setSearchConfig] = useState({ enabled: true, disabledMessage: "" });
  const [categories, setCategories] = useState<FrontendCategory[]>([]);
  const [listings, setListings] = useState<ListingItem[]>([]);
  const [loadingListings, setLoadingListings] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showSearchTip, setShowSearchTip] = useState(false);
  const [siteInfo, setSiteInfo] = useState<any>(null);

  // 加载站点信息
  useEffect(() => {
    getSiteInfo().then((info) => {
      if (info) {
        setSiteInfo(info);
        document.title = info.seo_title || '';
        if (info.search_enabled !== undefined) {
          setSearchConfig({
            enabled: info.search_enabled !== false,
            disabledMessage: info.search_disabled_message || "搜索功能已关闭",
          });
        }
      }
    });
  }, []);

  // 加载分类
  useEffect(() => {
    if (siteLoading || !siteId) return;
    getSiteCategories()
      .then((cats) => {
        setCategories(cats.slice(0, 12)); // 取前12个分类
      })
      .catch(console.error)
      .finally(() => setLoadingCategories(false));
  }, [siteId, siteLoading]);

  // 加载最新信息
  useEffect(() => {
    if (siteLoading || !siteId) return;
    getListings({ page_size: 8 })
      .then((data) => setListings(data.list || []))
      .catch(console.error)
      .finally(() => setLoadingListings(false));
  }, [siteId, siteLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (!searchConfig.enabled) {
      setShowSearchTip(true);
      setTimeout(() => setShowSearchTip(false), 3000);
      return;
    }
    if (searchQuery.trim()) navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      {/* ── Hero 全屏搜索区 ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800">
        {/* 装饰元素 */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400/20 rounded-full blur-3xl" />
          <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-indigo-400/15 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-500/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 md:py-24 lg:py-32 text-center">
          {/* 标题 */}
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-4">
            发现优质
            <span className="bg-gradient-to-r from-cyan-300 to-blue-200 bg-clip-text text-transparent"> 商业服务</span>
          </h1>
          <p className="text-base md:text-lg text-blue-100/80 max-w-2xl mx-auto mb-10 leading-relaxed">
            汇聚全城优质服务商，一键触达您需要的专业服务
          </p>

          {/* 搜索框 - 现代圆角大输入框 */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto relative">
            <div className="relative group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-400 to-blue-400 rounded-2xl opacity-30 group-hover:opacity-50 blur transition duration-500" />
              <div className="relative flex items-center bg-white rounded-xl shadow-2xl shadow-blue-900/20">
                <i className="ri-search-line pl-5 text-gray-400 text-xl" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="搜索服务、商家、关键词..."
                  className="flex-1 py-4 px-3 text-sm md:text-base text-gray-700 placeholder-gray-400 focus:outline-none rounded-l-xl"
                />
                <button
                  type="submit"
                  className="px-8 py-4 font-semibold text-sm md:text-base text-white rounded-r-xl bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600 hover:shadow-lg transition-all duration-300"
                >
                  搜索
                </button>
              </div>
            </div>
            {/* 搜索关闭提示 */}
            {showSearchTip && (
              <div className="absolute left-1/2 -translate-x-1/2 -bottom-10 mt-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg shadow-lg whitespace-nowrap z-10">
                <i className="ri-error-warning-line mr-1" />
                {searchConfig.disabledMessage || "搜索功能已关闭"}
              </div>
            )}
          </form>


        </div>

        {/* 底部波浪分隔线 */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
            <path d="M0 60L48 55C96 50 192 40 288 35C384 30 480 30 576 33.3C672 36.7 768 43.3 864 45C960 46.7 1056 43.3 1152 40C1248 36.7 1344 33.3 1392 31.7L1440 30V60H1392C1344 60 1248 60 1152 60C1056 60 960 60 864 60C768 60 672 60 576 60C480 60 384 60 288 60C192 60 96 60 48 60H0Z" fill="#f8fafc"/>
          </svg>
        </div>
      </section>

      {/* ── 服务分类 ── 图标卡片式 */}
      <section className="py-14 md:py-18">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800">服务分类</h2>
            <p className="text-sm text-gray-500 mt-2">快速找到您需要的服务类型</p>
          </div>

          {loadingCategories ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
              {[...Array(12)].map(i => (
                <div key={i} className="animate-pulse bg-white rounded-2xl p-5 flex flex-col items-center gap-3 shadow-sm border border-gray-100">
                  <div className="w-11 h-11 bg-gray-200 rounded-xl" />
                  <div className="w-12 h-3.5 bg-gray-200 rounded" />
                </div>
              ))}
            </div>
          ) : categories.length > 0 ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
              {categories.map(cat => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center gap-2.5 p-4 md:p-5 bg-white rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-300"
                >
                  <div className="w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 group-hover:from-blue-600 group-hover:to-indigo-600 group-hover:text-white transition-all duration-300 text-xl md:text-2xl">
                    {cat.icon ? <i className={`${cat.icon}`} /> : <i className="ri-apps-line" />}
                  </div>
                  <span className="text-xs md:text-sm font-medium text-gray-700 text-center line-clamp-1 group-hover:text-blue-600 transition-colors">{cat.name}</span>
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      </section>

      {/* ── 最新信息 ── 双列大卡片 */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-800">最新发布</h2>
              <p className="text-sm text-gray-500 mt-1">发现最新上线的优质服务</p>
            </div>
            <Link to="/category/all" className="hidden sm:flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors">
              查看全部 <i className="ri-arrow-right-line" />
            </Link>
          </div>

          {loadingListings ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {[...Array(4)].map(i => (
                <div key={i} className="animate-pulse bg-slate-50 rounded-2xl p-5 border border-gray-100">
                  <div className="flex gap-4">
                    <div className="w-28 h-28 bg-gray-200 rounded-xl shrink-0" />
                    <div className="flex-1 space-y-2.5">
                      <div className="w-3/4 h-5 bg-gray-200 rounded-lg" />
                      <div className="w-1/2 h-3.5 bg-gray-200 rounded" />
                      <div className="w-full h-3 bg-gray-100 rounded" />
                      <div className="w-2/3 h-3 bg-gray-100 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {listings.map(listing => (
                <Link
                  key={listing.id}
                  to={`/listing/${listing.id}`}
                  className="group flex gap-4 p-5 bg-slate-50 rounded-2xl border border-gray-100 hover:border-blue-200 hover:shadow-lg transition-all duration-300"
                >
                  {/* 图片 */}
                  <div className="w-28 h-28 shrink-0 rounded-xl overflow-hidden bg-gray-200 relative">
                    {listing.images?.[0] ? (
                      <img src={listing.images[0]} alt={listing.title} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-100 to-indigo-100">
                        <i className="ri-image-line text-2xl text-blue-300" />
                      </div>
                    )}
                    {listing.is_pinned && (
                      <span className="absolute top-2 left-2 px-1.5 py-0.5 bg-blue-600 text-white text-xs rounded-md font-medium">置顶</span>
                    )}
                  </div>

                  {/* 内容 */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
                    <div>
                      <h3 className="font-semibold text-gray-800 text-sm md:text-base line-clamp-1 group-hover:text-blue-600 transition-colors">
                        {listing.title}
                      </h3>
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        <span className="inline-flex px-2 py-0.5 bg-blue-50 text-blue-600 text-xs rounded-md font-medium">
                          {listing.category?.name || '未分类'}
                        </span>
                        <span className="text-xs text-gray-400">{formatRelativeTime(listing.created_at)}</span>
                      </div>
                      <p className="text-gray-500 text-xs md:text-sm mt-2 line-clamp-2 leading-relaxed">{listing.content}</p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-user-line text-xs text-gray-400 shrink-0" />
                        <span className="text-xs text-gray-500 truncate max-w-[80px]">{listing.contact_name || '匿名'}</span>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <i className="ri-eye-line" />
                        <span>{listing.view_count}</span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-2xl bg-blue-50">
                <i className="ri-inbox-line text-2xl text-blue-400" />
              </div>
              <p className="text-gray-500">暂无信息</p>
            </div>
          )}

          <div className="mt-8 text-center sm:hidden">
            <Link to="/category/all" className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700">
              查看全部发布 <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA 行动号召区 ── */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-indigo-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">有优质服务要推广？</h2>
          <p className="text-blue-100 mb-8">发布您的服务信息，让更多客户找到您</p>
          <button
            onClick={() => navigate("/publish")}
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-white text-blue-600 font-semibold rounded-full hover:bg-blue-50 hover:shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-0.5"
          >
            <i className="ri-add-circle-line" />
            立即发布
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}

// 工具函数
function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const diff = Date.now() - date.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "昨天";
  if (days < 7) return `${days}天前`;
  if (days < 30) return `${Math.floor(days / 7)}周前`;
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}
